import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { CACHE_PRESETS } from '@/constants';
import { useAuth } from '@/hooks/auth';
import { useFilterManagement } from '@/hooks/common/use-filter-management';
import DashboardService from '@/services/dashboard';
import type { DashboardFilters } from '@/types/dashboard';
import { message } from 'antd';

const DASHBOARD_KEYS = {
  all: ['dashboard'] as const,
  stats: (filters: DashboardFilters) => [...DASHBOARD_KEYS.all, 'stats', filters] as const,
  charts: (filters: DashboardFilters) => [...DASHBOARD_KEYS.all, 'charts', filters] as const,
};

export const useDashboard = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { filters, setFilter, clearAllFilters } = useFilterManagement<DashboardFilters>({
    initialFilters: {},
  });

  const {
    data: stats,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: DASHBOARD_KEYS.stats(filters),
    queryFn: () => DashboardService.getDashboardStats(filters),
    enabled: isAuthenticated,
    staleTime: CACHE_PRESETS.STANDARD.staleTime,
    gcTime: CACHE_PRESETS.STANDARD.gcTime,
    retry: (failureCount, queryError: unknown) => {
      const apiError = queryError as { response?: { status?: number } };
      if (apiError?.response?.status === 401) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const {
    data: chartsData,
    isLoading: chartsLoading,
    error: chartsError,
  } = useQuery({
    queryKey: DASHBOARD_KEYS.charts(filters),
    queryFn: () => DashboardService.getDashboardCharts(filters),
    enabled: isAuthenticated,
    staleTime: CACHE_PRESETS.STANDARD.staleTime,
    gcTime: CACHE_PRESETS.STANDARD.gcTime,
    retry: (failureCount, queryError: unknown) => {
      const apiError = queryError as { response?: { status?: number } };
      if (apiError?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const [refreshedStats, charts] = await Promise.all([
        DashboardService.getDashboardStats(filters),
        DashboardService.getDashboardCharts(filters),
      ]);
      return { stats: refreshedStats, charts };
    },
    onSuccess: ({ stats: refreshedStats, charts }) => {
      queryClient.setQueryData(DASHBOARD_KEYS.stats(filters), refreshedStats);
      queryClient.setQueryData(DASHBOARD_KEYS.charts(filters), charts);
      message.success('Dashboard data refreshed successfully');
    },
    onError: () => {
      message.error('Failed to refresh dashboard data');
    },
  });

  const handleDateRangeChange = useCallback(
    (dates: [import('dayjs').Dayjs | null, import('dayjs').Dayjs | null] | null) => {
      if (dates?.[0] && dates[1]) {
        setFilter('dateRange', [dates[0].toISOString(), dates[1].toISOString()]);
      } else {
        setFilter('dateRange', undefined);
      }
    },
    [setFilter]
  );

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
  }, [clearAllFilters]);

  const handleRefresh = useCallback(() => {
    if (isAuthenticated) {
      refreshMutation.mutate();
    }
  }, [isAuthenticated, refreshMutation]);

  const errorMessage = useMemo(() => {
    if (!isAuthenticated) {
      return 'User not authenticated';
    }
    if (error) {
      return 'Failed to load dashboard data. Please try again.';
    }
    return null;
  }, [isAuthenticated, error]);

  const chartsErrorMessage = useMemo(() => {
    if (!isAuthenticated) {
      return 'Failed to load dashboard charts. Please try again.';
    }
    if (chartsError) {
      return 'Failed to load dashboard charts. Please try again.';
    }
    return null;
  }, [isAuthenticated, chartsError]);

  return {
    stats: stats || null,
    chartsData: chartsData || null,
    loading,
    chartsLoading,
    error: errorMessage,
    chartsError: chartsErrorMessage,
    filters,
    handleDateRangeChange,
    handleClearFilters,
    handleRefresh,
    isRefreshing: refreshMutation.isPending,
    invalidateQueries: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEYS.all });
    },
    refetch,
  };
};
